import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { RouterLink, Router } from '@angular/router';
import { RestaurantService } from 'src/app/shared/services/restaurant/restaurant.service';
import { RestaurantConfig } from 'src/app/shared/interfaces/restaurant.interface';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { StorageService } from 'src/app/shared/services/storage/storage.service';
import { RolesService } from 'src/app/shared/services/roles/roles.service';
import { LoaderComponent } from 'src/app/shared/components/loader/loader.component';
import { LoaderService } from 'src/app/shared/services/loader/loader.service';

type BlockedDate = {
  id: number;
  created_when: string;
  is_deleted: boolean;
  deleted_at: string | null;
  date: string; // "YYYY-MM-DD"
  block_type: 'EVENT' | 'HOLIDAY' | 'NO_SERVICE' | string;
  reason: string;
  author: number;
};

@Component({
  selector: 'app-reservation-config',
  imports: [
    MatIconModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    RouterLink,
    FormsModule,
    ReactiveFormsModule,
    LoaderComponent,
  ],
  templateUrl: './reservation-config.component.html',
  styleUrls: [
    './reservation-config.component.css',
    '../../../shared/styles/layout.css',
  ],
})
export class ReservationConfigComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  configForm: FormGroup;
  isSubmitting = false;

  config: RestaurantConfig = {
    id: 0,
    extra_menus: 0,
    start_date: '',
    end_date: null,
  };

  private _snackBar = inject(MatSnackBar);

  myroles: any;

  private blockedDates: BlockedDate[] = [];
  private blockedSet: Set<string> = new Set(); // YYYY-MM-DD de días bloqueados activos
  private targetDate: Date = new Date(); // Fecha objetivo calculada
  private lastCheckedConfigId: number | null = null; // Track last config ID to detect infinite loops

  constructor(
    private restaurantService: RestaurantService,
    private fb: FormBuilder,
    private router: Router,
    private snackBar: MatSnackBar,
    private rolesService: RolesService,
    private storageService: StorageService,
    private loaderService: LoaderService
  ) {
    this.configForm = this.fb.group({
      extra_menus: [
        0,
        [Validators.required, Validators.min(0), Validators.max(100)],
      ],
    });
  }

  get isLoading(): boolean {
    return this.loaderService.getLoader();
  }

  ngOnInit() {
    this.loadBlockedDatesAndThenConfig();
  }

  ngOnDestroy() {
    // Cleanup subscriptions
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Format date for API (YYYY-MM-DD)
  formatDateForApi(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // ---------- Helper methods for date calculation ----------
  private toMidnight(d: Date): Date {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  }

  private isWeekend(d: Date): boolean {
    const day = d.getDay(); // 0 = domingo, 6 = sábado
    return day === 0 || day === 6;
  }

  private isBlocked(d: Date): boolean {
    const ymd = this.formatDateForApi(d);
    return this.blockedSet.has(ymd);
  }

  private getNextWorkingDay(from: Date, daysToAdd: number = 1): Date {
    // Start from the specified number of days after 'from'
    let d = this.toMidnight(from);
    d.setDate(d.getDate() + daysToAdd);

    // Skip weekends and blocked dates
    while (this.isWeekend(d) || this.isBlocked(d)) {
      d.setDate(d.getDate() + 1);
    }
    return d;
  }

  loadBlockedDatesAndThenConfig() {
    this.loaderService.showLoader();

    this.restaurantService
      .getBlockedDays()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blockedDates: BlockedDate[]) => {
          // Guardamos bloqueos activos (no eliminados) en un Set para lookup rápido
          this.blockedDates = blockedDates || [];
          this.blockedSet = new Set(
            this.blockedDates
              .filter((b) => !b.is_deleted && !!b.date)
              .map((b) => b.date.trim())
          );

          // Check if menus were sent for next working day
          this.checkIfMenusSentAndCalculateTarget();
        },
        error: (error) => {
          console.error('Error loading blocked dates:', error);
          // Si falla, al menos calcula el siguiente laborable sin bloqueos (sólo fin de semana)
          const now = new Date();
          this.targetDate = this.getNextWorkingDay(now, 1);
          this.getConfig();
        },
      });
  }

  private checkIfMenusSentAndCalculateTarget() {
    const now = new Date();
    const nextWorkingDay = this.getNextWorkingDay(now, 1);

    // Reset the tracking variable before starting the search
    this.lastCheckedConfigId = null;

    // Find the first working day where menus_sent is not true
    this.findFirstDayWithoutMenusSent(nextWorkingDay);
  }

  private findFirstDayWithoutMenusSent(checkDate: Date) {
    const checkDateStr = this.formatDateForApi(checkDate);

    // Check if a config exists for this working day with menus_sent=true
    this.restaurantService
      .getRestaurantConfig(checkDateStr)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (config) => {
          const menusSent = config?.menus_sent === true;
          const currentConfigId = config?.id;

          // console.log(
          //   `Checking date (${checkDateStr}): menus_sent=${menusSent}, config_id=${currentConfigId}`
          // );

          // Detect infinite loop: If we receive the same config ID twice in a row,
          // it means the API is returning the same config object for dates that don't exist yet.
          // In this case, stop recursion and use the current date.
          if (currentConfigId !== undefined && currentConfigId === this.lastCheckedConfigId) {
            // console.log(
            //   `Detected same config ID (${currentConfigId}) twice - stopping recursion at ${checkDateStr}`
            // );
            // Found the boundary - this is the first day without a real config
            this.targetDate = checkDate;
            this.config = config;
            this.configForm.patchValue({
              extra_menus: config.extra_menus,
            });
            this.loaderService.hideLoader();
            return;
          }

          // Update last checked config ID for next iteration
          this.lastCheckedConfigId = currentConfigId;

          if (menusSent) {
            // Menus already sent for this day, check the next working day
            const nextDay = this.getNextWorkingDay(checkDate, 1);
            this.findFirstDayWithoutMenusSent(nextDay);
          } else {
            // Found a day where menus_sent is false or undefined
            this.targetDate = checkDate;
            // Use the config we already fetched instead of making another API call
            this.config = config;
            this.configForm.patchValue({
              extra_menus: config.extra_menus,
            });
            this.loaderService.hideLoader();
          }
        },
        error: (err) => {
          // No config found for this day, so menus not sent - use this date
          // console.log(`No config found for ${checkDateStr}, menus not sent`);
          this.targetDate = checkDate;
          this.getConfig();
        },
      });
  }

  getConfig() {
    const targetApiDate = this.formatDateForApi(this.targetDate);

    this.restaurantService
      .getRestaurantConfig(targetApiDate)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (config) => {
          this.config = config;
          // Update form with received values
          this.configForm.patchValue({
            extra_menus: config.extra_menus,
          });
          this.loaderService.hideLoader();
        },
        error: (err) => {
          console.error('Error fetching restaurant config:', err);
          this.snackBar.open('Error al cargar la configuración', 'Cerrar', {
            duration: 3000,
          });
          this.loaderService.hideLoader();
        },
      });
  }

  loadRoles() {
    // Get permissions using the service method
    const permissions = this.rolesService.getPermissionsForFunctionality(
      'general',
      this.storageService
    );

    if (permissions) {
      // Store permissions in myroles
      this.myroles = permissions;

      // Check write permission and redirect if needed
      this.rolesService.checkWritePermission(
        'general',
        this.storageService,
        this._snackBar,
        this.router,
        '/home/dining/general',
        'No tienes permisos para modificar los menus extra'
      );
    } else {
      //   console.log('No user roles found in storage');
    }
  }

  saveChanges(): void {
    if (this.isSubmitting) return;

    if (!this.configForm.valid) {
      // Mark all form controls as touched to trigger validation display
      Object.keys(this.configForm.controls).forEach((key) => {
        const control = this.configForm.get(key);
        control?.markAsTouched();
      });

      this.snackBar.open(
        'Por favor completa todos los campos requeridos',
        'Cerrar',
        { duration: 3000 }
      );
      return;
    }

    this.isSubmitting = true;

    // Prepare the data to send with the calculated target date
    const updateData: any = {
      extra_menus: this.configForm.get('extra_menus')?.value,
      start_date: this.formatDateForApi(this.targetDate),
    };

    // Call the service to update the config
    this.restaurantService
      .postConfigDaily(updateData)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isSubmitting = false))
      )
      .subscribe({
        next: (response: any) => {
          this.snackBar.open(
            'Configuración actualizada exitosamente',
            'Cerrar',
            {
              duration: 3000,
            }
          );
          this.router.navigate(['/home/dining/general']);
        },
        error: (error: any) => {
          console.error('Error updating config:', error);
          this.snackBar.open('Error al actualizar la configuración', 'Cerrar', {
            duration: 3000,
          });
        },
      });
  }

  cancel(): void {
    this.router.navigate(['/home/dining/general']);
  }
}
